"use client";

import { useState, useEffect } from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { getChipColor } from "../../components/ChipToken";

import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { useSelector } from "react-redux";

const pad = (n) => String(n).padStart(2, "0");
const nowStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function CreateTipDrawer({ open, onClose, refresh }) {
  const token = useSelector((state) => state.loginSlice.token);

  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");
  const [onTableChips, setOnTableChips] = useState({});
  const [employees, setEmployees]       = useState([]);

  const [form, setForm] = useState({
    employee_name: "",
    date:          nowStr(),
    chips_in:      [],
    remarks:       "",
    override_reason: "",
  });
  const [declaration, setDeclaration] = useState(null); // dealer's own declared handover, if any
  const [declarationChecked, setDeclarationChecked] = useState(false);

  useEffect(() => {
    if (!open) return;

    setForm((f) => ({ ...f, date: nowStr(), employee_name: "", remarks: "", override_reason: "" }));
    setDeclaration(null);
    setDeclarationChecked(false);
    setError("");

    Promise.all([
      axiosRequest("GET", `${apiBaseUrl}/chips`, token),
      axiosRequest("GET", `${apiBaseUrl}/shift-management/current-summary`, token),
      axiosRequest("GET", `${apiBaseUrl}/employees`, token),
    ]).then(([chipsRes, summaryRes, employeesRes]) => {
        const rawChips = Array.isArray(chipsRes?.data) ? chipsRes.data : (chipsRes?.data?.data ?? []);
        // Deduplicate by denomination — keep highest quantity per denomination
        const denomMap = {};
        rawChips.forEach(c => {
          if (!denomMap[c.denomination] || c.quantity > denomMap[c.denomination].quantity) denomMap[c.denomination] = c;
        });
        const rows = Object.values(denomMap)
          .sort((a, b) => a.denomination - b.denomination)
          .map((c) => ({ denomination: c.denomination, quantity: "" }));
        setForm((f) => ({ ...f, chips_in: rows }));
        setOnTableChips(summaryRes?.data?.on_table_chips || {});
        const employeeList = Array.isArray(employeesRes?.data) ? employeesRes.data : [];
        setEmployees(employeeList.filter((e) => e.status !== false));
      })
      .catch(() => {});
  }, [open]);

  // Pull the dealer's own declared handover (if any) so the cashier sees a live
  // match/mismatch instead of finding out only after trying to save.
  useEffect(() => {
    setDeclaration(null);
    setDeclarationChecked(false);
    if (!form.employee_name) return;
    let cancelled = false;
    axiosRequest("GET", `${apiBaseUrl}/tip/declaration-lookup?employee_name=${encodeURIComponent(form.employee_name)}`, token)
      .then((res) => {
        if (cancelled) return;
        setDeclaration(res?.data?.declaration || null);
        setDeclarationChecked(true);
      })
      .catch(() => { if (!cancelled) setDeclarationChecked(true); });
    return () => { cancelled = true; };
  }, [form.employee_name, token]);

  const calcAmount = (denom, qty) => Number(denom || 0) * Number(qty || 0);

  const totalIn = form.chips_in.reduce(
    (sum, c) => sum + calcAmount(c.denomination, c.quantity), 0
  );

  const hasMismatch = declarationChecked && form.employee_name && totalIn > 0 && (
    declaration ? declaration.total_amount !== totalIn : true
  );

  const handleQtyChange = (index, value) => {
    const updated = [...form.chips_in];
    updated[index] = { ...updated[index], quantity: value };
    setForm({ ...form, chips_in: updated });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError("");
      const payload = { ...form, date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(), total_in: totalIn, rake_amount: totalIn, total_tips: 0 };
      const res = await axiosRequest("POST", `${apiBaseUrl}/tip`, token, payload);
      if (res?.status === "fail" || res?.status === false) {
        setError(res?.message || "Failed to create tip.");
        return;
      }
      refresh();
      onClose();
    } catch (err) {
      console.log(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 650, p: 3 }}>

        <Box mb={3}>
          <Typography variant="h5" fontWeight={700}>Create Tip</Typography>
          <Typography color="text.secondary">Manage employee tips</Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>

          {/* EMPLOYEE NAME */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth size="small"
              options={employees}
              getOptionLabel={(opt) => opt.name || ""}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              value={employees.find((e) => e.name === form.employee_name) || null}
              onChange={(_, newValue) => setForm({ ...form, employee_name: newValue?.name || "" })}
              renderOption={(props, opt) => (
                <li {...props} key={opt._id}>
                  {opt.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({opt.designation})</Typography>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Employee Name" placeholder="Select employee" />}
              noOptionsText="No employees found — add them in Employee Manager"
            />
          </Grid>

          {/* DATE & TIME */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small" type="datetime-local" label="Date & Time"
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </Grid>

          {/* CHIPS IN */}
          <Grid item xs={12}>
            <Typography fontWeight={700} mb={1}>Chips In</Typography>
          </Grid>

          {form.chips_in.map((chip, index) => {
            const cc      = getChipColor(chip.denomination);
            const onTable = Number(onTableChips[chip.denomination] || 0);
            const isOver  = Number(chip.quantity) > onTable;
            return (
            <Grid container spacing={2} key={index} mb={1} px={1} alignItems="center">
              <Grid item xs={3}>
                <Box sx={{ display: "flex", alignItems: "center", height: "100%", px: 1.5, py: 1, borderRadius: 2, bgcolor: cc.bg, color: cc.text }}>
                  <Typography fontWeight={700} fontSize="0.95rem">{chip.denomination}</Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth size="small" label="On Table"
                  value={onTable}
                  InputProps={{ readOnly: true }}
                  sx={{ "& .MuiInputBase-input": { color: onTable === 0 ? "error.main" : "text.secondary", fontWeight: 600 } }}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth size="small" type="number" label="Quantity"
                  value={chip.quantity}
                  onChange={(e) => handleQtyChange(index, e.target.value)}
                  inputProps={{ min: 0, max: onTable }}
                  disabled={onTable === 0}
                  error={isOver}
                  helperText={isOver ? `Max ${onTable}` : ""}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth size="small" label="Amount ()"
                  value={calcAmount(chip.denomination, chip.quantity) > 0
                    ? `${calcAmount(chip.denomination, chip.quantity).toLocaleString()}`
                    : ""}
                  InputProps={{ readOnly: true }}
                  sx={{ "& .MuiInputBase-input": { color: "success.main", fontWeight: 600 } }}
                />
              </Grid>
            </Grid>
          );})}

          {/* SUMMARY */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 2, mt: 1, background: totalIn > 0 ? "linear-gradient(135deg,#11998e,#38ef7d)" : "#f5f5f5" }}>
              <Typography fontWeight={700} color={totalIn > 0 ? "#fff" : "text.primary"}>
                Total Chips In: {totalIn.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>

          {/* DEALER DECLARATION CHECK */}
          {form.employee_name && declarationChecked && (
            <Grid item xs={12}>
              {declaration ? (
                <Alert severity={declaration.total_amount === totalIn ? "success" : "warning"}>
                  {form.employee_name} declared <b>₹{declaration.total_amount.toLocaleString()}</b> from the dealer page
                  {declaration.total_amount === totalIn
                    ? " — matches this entry."
                    : ` — this entry is ₹${totalIn.toLocaleString()}, a difference of ₹${Math.abs(declaration.total_amount - totalIn).toLocaleString()}.`}
                </Alert>
              ) : (
                <Alert severity="warning">No declaration on file for {form.employee_name} — have them submit one from the dealer page, or enter a reason below to proceed.</Alert>
              )}
            </Grid>
          )}

          {/* OVERRIDE REASON — required when entry doesn't match (or there's no) declaration */}
          {hasMismatch && (
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Reason for mismatch *" required
                placeholder="e.g. dealer recount, declaration entered wrong table"
                value={form.override_reason}
                onChange={(e) => setForm({ ...form, override_reason: e.target.value })}
              />
            </Grid>
          )}

          {/* REMARKS */}
          <Grid item xs={12}>
            <TextField
              fullWidth multiline rows={3} size="small" label="Remarks"
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            />
          </Grid>

          {/* ERROR */}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
            </Grid>
          )}

          {/* ACTIONS */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={onClose}>Cancel</Button>
              <Button variant="contained" onClick={handleSave}
                disabled={loading || form.chips_in.some(c => Number(c.quantity) > Number(onTableChips[c.denomination] || 0)) || (hasMismatch && !form.override_reason.trim())}
              >
                {loading ? "Saving..." : "Create Tip"}
              </Button>
            </Box>
          </Grid>

        </Grid>
      </Box>
    </Drawer>
  );
}
